import React, { useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../utils/customToast';
import { FiAlertCircle } from 'react-icons/fi';
import { usePlanPermissions, getCurrentPlan, UpgradeMessage } from '../../utils/planPermissions';
import Modal from '../shared/Modal';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const ImportModal = ({ open, onClose, defaultType = 'messages', onImportSuccess }) => {
    const { getAuthHeaders, activeTenantId, hasTenantRole, user } = useAuth();
    const [exportType, setExportType] = useState(defaultType);
    const [format, setFormat] = useState('csv');
    const [importFile, setImportFile] = useState(null);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canImport } = usePlanPermissions(currentPlan);

    if (!open) return null;

    const canImportRole = hasTenantRole(activeTenantId, ['owner', 'admin']);
    const canPerformImport = canImport && canImportRole;

    const handleFileChange = (e) => {
        setImportFile(e.target.files?.[0] || null);
        setError(null); // Réinitialiser l'erreur lors du changement de fichier
    };

    // CSV parser (quoted fields, delimiter param)
    const parseCSV = (text, delimiter = ';') => {
        const rows = [];
        let cur = '';
        let row = [];
        let i = 0;
        let inQuotes = false;

        while (i < text.length) {
            const ch = text[i];
            const next = text[i + 1];

            if (ch === '"') {
                if (inQuotes && next === '"') {
                    cur += '"';
                    i += 2;
                    continue;
                }
                inQuotes = !inQuotes;
                i++;
                continue;
            }

            if (!inQuotes && ch === delimiter) {
                row.push(cur);
                cur = '';
                i++;
                continue;
            }

            if (!inQuotes && (ch === '\n' || (ch === '\r' && text[i + 1] === '\n'))) {
                row.push(cur);
                rows.push(row);
                row = [];
                cur = '';
                if (ch === '\r' && text[i + 1] === '\n') i += 2; else i++;
                continue;
            }

            cur += ch;
            i++;
        }

        if (cur !== '' || inQuotes || row.length > 0) {
            row.push(cur);
            rows.push(row);
        }

        const cleaned = rows.filter(r => !(r.length === 1 && String(r[0]).trim() === ''));
        if (cleaned.length === 0) return [];
        const headers = cleaned[0].map(h => String(h || '').trim());
        const dataRows = cleaned.slice(1);
        return dataRows.map(r => {
            const obj = {};
            headers.forEach((h, idx) => {
                const key = String(h || '').trim();
                obj[key] = (r[idx] ?? '').replace(/""/g, '"');
            });
            return obj;
        });
    };

    const handleImport = async () => {
        if (!canPerformImport) {
            return;
        }
        if (!importFile) {
            setError(`Sélectionnez un fichier ${format.toUpperCase()} à importer.`);
            return;
        }

        setError(null); // Réinitialiser l'erreur avant de commencer

        try {
            const text = await importFile.text();
            let items = [];

            if (format === 'csv') {
                items = parseCSV(text, ';');
                if (items.length === 0) items = parseCSV(text, ',');
            } else if (format === 'json') {
                let parsed = null;
                try {
                    parsed = JSON.parse(text);
                } catch (e) {
                    throw new Error('Impossible de parser le JSON');
                }

                if (Array.isArray(parsed)) {
                    items = parsed;
                } else if (parsed && Array.isArray(parsed.items)) {
                    items = parsed.items;
                } else {
                    throw new Error('Format JSON invalide, un tableau est attendu');
                }
            }

            const mapValue = (obj, keys, fallback = '') => {
                for (const k of keys) {
                    if (obj.hasOwnProperty(k)) return obj[k];
                    const lk = k.toLowerCase();
                    const found = Object.keys(obj).find(x => String(x).toLowerCase() === lk);
                    if (found) return obj[found];
                }
                return fallback;
            };

            let payloadItems = [];
            if (exportType === 'messages') {
                payloadItems = items.map(it => ({
                    title: mapValue(it, ['title', 'TITLE', 'Title']),
                    content: mapValue(it, ['content', 'CONTENT', 'body', 'Body']),
                    type: mapValue(it, ['type', 'TYPE']) || 'Information',
                    role: mapValue(it, ['role', 'ROLE']) || 'Système'
                }));
            } else {
                payloadItems = items.map(it => ({
                    text: mapValue(it, ['text', 'TEXT']) || '',
                    done: String((mapValue(it, ['done', 'DONE']) || '')).toLowerCase() === 'true',
                    author: mapValue(it, ['author', 'AUTHOR']) || ''
                }));
            }

            const url = `${apiBase}/${exportType}/import`;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ items: payloadItems })
            });

            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = (json && json.message) ? json.message : 'Erreur lors de l\'import.';

                // Pour les erreurs de limite, afficher uniquement dans la modale
                if (json.code === 'LIMIT_REACHED' || json.code === 'PARTIAL_LIMIT') {
                    setError(msg);
                    return;
                }

                // Pour les autres erreurs, afficher dans la modale
                setError(msg);
                return;
            }

            const created = json.created || (Array.isArray(payloadItems) ? payloadItems.length : 0);
            toast.success(`Import réussi (${created} éléments créés)`, { position: 'top-right' });
            setImportFile(null);
            setError(null);
            if (fileRef.current) fileRef.current.value = '';
            try { if (typeof onImportSuccess === 'function') await onImportSuccess(); } catch (e) { console.warn('Refresh after import failed', e); }
            onClose();
        } catch (err) {
            console.error('Import error', err);
            // Afficher les erreurs de parsing uniquement dans la modale
            if (err.message?.toLowerCase?.().includes('json') || err.message?.toLowerCase?.().includes('parse')) {
                setError('Fichier invalide ou format incorrect.');
            } else {
                setError(err?.message || 'Erreur lors de l\'import.');
            }
        }
    };

    // Modal actions
    const modalActions = (
        <>
            {canPerformImport && (
                <button
                    ref={confirmRef}
                    onClick={handleImport}
                    disabled={!importFile}
                    className="flex-1 bg-accent text-white dark:text-black py-2 px-4 rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Importer ({format.toUpperCase()})
                </button>
            )}

            <button
                ref={cancelRef}
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded border dark:text-white"
            >
                Fermer
            </button>
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`Importer (${format.toUpperCase()})`}
            actions={modalActions}
            initialFocusRef={confirmRef}
        >
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Type</label>
                    <select
                        value={exportType}
                        onChange={e => {
                            setExportType(e.target.value);
                            setError(null);
                        }}
                        className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="messages">Messages</option>
                        <option value="todos">Todos</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Format</label>
                    <select
                        value={format}
                        onChange={e => { setFormat(e.target.value); setError(null); }}
                        className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="csv">CSV</option>
                        <option value="json">JSON</option>
                    </select>
                </div>
            </div>

            {/* Input file placé sous le select, au-dessus des boutons */}
            <div className="mb-4">
                <input
                    ref={fileRef}
                    type="file"
                    accept={format === 'csv' ? '.csv' : '.json'}
                    onChange={handleFileChange}
                    className="text-sm w-full"
                    aria-label={`Sélectionner fichier ${format.toUpperCase()}`}
                />
            </div>

            {/* Affiche le nom du fichier sélectionné */}
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                Fichier sélectionné : {importFile?.name || 'Aucun fichier'}
            </div>

            {/* Restriction de plan */}
            {!canImport && (
                <UpgradeMessage
                    feature="imports"
                    currentPlan={currentPlan}
                    className="mb-4"
                />
            )}

            {/* Affichage des erreurs dans la modale */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                    <FiAlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
            )}
        </Modal>
    );
};

export default ImportModal;